import React, { useEffect, useState } from 'react'
import {
  Alert, Avatar, Box, Button, Card, CardContent, Chip, CircularProgress,
  Divider, Link as MuiLink, Typography,
} from '@mui/material'
import VideocamIcon   from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import EventIcon      from '@mui/icons-material/Event'
import PlayCircleIcon from '@mui/icons-material/PlayCircleOutlined'
import { supabase } from '../../supabaseClient'
import { useAuth }  from '../../auth/AuthContext'
import { useViewerStudent } from '../../auth/ViewAsContext'
import { readFunctionError } from '../../lib/functionError'
import ClassOfficeHoursSection from '../../components/classes/ClassOfficeHoursSection'

interface Meeting {
  meeting_id:    string
  kind:          'group' | 'office_hours'
  scheduled_at:  string
  duration_min:  number
  format:        'in_person' | 'online' | 'simulcast'
  location:      string
  zoom_id:       string | null
  zoom_passcode: string | null
  zoom_url:      string | null
  recording_url: string | null
  host_name:     string | null
  status:        'scheduled' | 'cancelled' | 'held'
  notes:         string | null
  my_status:     string | null
}

interface ClassInfo {
  class_id:   string
  name:       string
  status:     string
  start_date: string | null
  end_date:   string | null
}

interface ClassMentorInfo {
  user_id:    string
  first_name: string
  last_name:  string
  role:       string   // 'lead' = primary
}

interface Mentor {
  user_id:    string
  first_name: string
  last_name:  string
  avatar_url: string | null
  email:      string
  kind:       'primary' | 'class'
}

const FORMAT_LABEL: Record<string, string> = {
  in_person: 'In-person',
  online:    'Online',
  simulcast: 'Simulcast',
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
  })
}

// DATE strings (YYYY-MM-DD) parsed locally to avoid a timezone shift.
function fmtDateOnly(s: string) {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function MyClassPage() {
  const { session } = useAuth()
  const { studentId: viewerStudentId, viewerIsStudent, loading: vasLoading } = useViewerStudent()
  const [cls,         setCls]         = useState<ClassInfo | null>(null)
  const [mentors,     setMentors]     = useState<Mentor[]>([])
  const [classMentors, setClassMentors] = useState<ClassMentorInfo[]>([])
  const [meetings,    setMeetings]    = useState<Meeting[]>([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState<string | null>(null)
  const [actioning,   setActioning]   = useState<string | null>(null)

  async function load() {
    if (!session || vasLoading) return
    setLoading(true)

    if (!viewerStudentId) { setCls(null); setLoading(false); return }
    const sp = { student_id: viewerStudentId }

    const { data: enrollment } = await supabase
      .from('class_enrollment')
      .select('class_id')
      .eq('student_id', sp.student_id)
      .eq('status', 'active')
      .maybeSingle()
    if (!enrollment?.class_id) { setCls(null); setLoading(false); return }

    const [{ data: classRow }, { data: classMentorRows }, { data: meetingRows }, { data: studentMentorRows }, { data: myAttendance }] = await Promise.all([
      supabase.from('class').select('class_id, name, status, start_date, end_date').eq('class_id', enrollment.class_id).maybeSingle(),
      supabase.from('class_mentor').select('user_id, role').eq('class_id', enrollment.class_id),
      supabase
        .from('class_meeting')
        .select('meeting_id, kind, scheduled_at, duration_min, format, location, zoom_id, zoom_passcode, zoom_url, recording_url, host_mentor_id, status, notes')
        .eq('class_id', enrollment.class_id)
        .neq('status', 'cancelled')
        .order('scheduled_at'),
      supabase
        .from('student_mentor')
        .select('mentor_id')
        .eq('student_id', sp.student_id)
        .eq('mentor_role', 'lead')
        .eq('status', 'active'),
      supabase
        .from('class_meeting_attendance')
        .select('meeting_id, status')
        .eq('student_id', sp.student_id),
    ])

    setCls(classRow as ClassInfo | null)

    // Resolve mentors: primary (lead) + class mentors
    const primaryIds = (studentMentorRows ?? []).map(r => r.mentor_id)
    const classMentorIds = (classMentorRows ?? []).map(r => r.user_id)
    const allMentorIds = Array.from(new Set([...primaryIds, ...classMentorIds]))
    if (allMentorIds.length > 0) {
      const { data: mentorUsers } = await supabase
        .from('users')
        .select('user_id, first_name, last_name, avatar_url, email')
        .in('user_id', allMentorIds)
      const userMap = Object.fromEntries((mentorUsers ?? []).map(u => [u.user_id, u]))
      const list: Mentor[] = (mentorUsers ?? []).map(m => ({
        ...m,
        kind: primaryIds.includes(m.user_id) ? 'primary' : 'class',
      }))
      list.sort((a, b) => (a.kind === 'primary' ? -1 : 1))
      setMentors(list)

      // Class mentors with their class role (for the office-hours section).
      const roleMap: Record<string, string> = Object.fromEntries((classMentorRows ?? []).map(r => [r.user_id, r.role]))
      setClassMentors(
        (classMentorRows ?? []).map(r => ({
          user_id:    r.user_id,
          first_name: userMap[r.user_id]?.first_name ?? '',
          last_name:  userMap[r.user_id]?.last_name ?? '',
          role:       roleMap[r.user_id] ?? 'mentor',
        }))
      )
    } else {
      setMentors([])
      setClassMentors([])
    }

    // Resolve host names for meetings
    const hostIds = Array.from(new Set((meetingRows ?? []).map(m => m.host_mentor_id).filter(Boolean) as string[]))
    let hostMap: Record<string, string> = {}
    if (hostIds.length > 0) {
      const { data: hosts } = await supabase
        .from('users')
        .select('user_id, first_name, last_name')
        .in('user_id', hostIds)
      hostMap = Object.fromEntries((hosts ?? []).map(h => [
        h.user_id,
        `${h.first_name ?? ''} ${h.last_name ?? ''}`.trim() || h.user_id,
      ]))
    }
    const myAttendanceMap: Record<string, string> = Object.fromEntries(
      (myAttendance ?? []).map(a => [a.meeting_id, a.status])
    )

    const merged: Meeting[] = (meetingRows ?? []).map(m => ({
      meeting_id:    m.meeting_id,
      kind:          m.kind,
      scheduled_at:  m.scheduled_at,
      duration_min:  m.duration_min,
      format:        m.format,
      location:      m.location,
      zoom_id:       m.zoom_id,
      zoom_passcode: m.zoom_passcode,
      zoom_url:      m.zoom_url,
      recording_url: m.recording_url,
      host_name:     m.host_mentor_id ? hostMap[m.host_mentor_id] ?? null : null,
      status:        m.status,
      notes:         m.notes,
      my_status:     myAttendanceMap[m.meeting_id] ?? null,
    }))
    setMeetings(merged)
    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [session, viewerStudentId, vasLoading])

  async function handleRSVP(meetingId: string, attend: boolean) {
    setActioning(meetingId)
    setError(null)
    const { data, error: invokeErr } = await supabase.functions.invoke('signup-office-hour', {
      body: { meeting_id: meetingId, attend },
    })
    setActioning(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>

  if (!cls) {
    return (
      <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>My Class</Typography>
        <Card variant="outlined">
          <CardContent>
            <Typography color="text.secondary">
              You're not currently enrolled in a class. Talk to your office to get assigned.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    )
  }

  const now = Date.now()
  const upcoming = meetings.filter(m => new Date(m.scheduled_at).getTime() >= now)
  const past     = meetings.filter(m => new Date(m.scheduled_at).getTime() <  now)

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 820 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>My Class</Typography>
      <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>{cls.name}</Typography>
      {(cls.start_date || cls.end_date) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {cls.start_date && fmtDateOnly(cls.start_date)}
          {cls.start_date && cls.end_date && ' – '}
          {cls.end_date && fmtDateOnly(cls.end_date)}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Mentors */}
      {mentors.length > 0 && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
              Your Mentor{mentors.length > 1 ? 's' : ''}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {mentors.map(m => {
                const fullName = `${m.first_name ?? ''} ${m.last_name ?? ''}`.trim() || m.email
                const initials = `${m.first_name?.[0] ?? ''}${m.last_name?.[0] ?? ''}`.toUpperCase()
                return (
                  <Box key={m.user_id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      src={m.avatar_url ?? undefined}
                      sx={{ width: 48, height: 48, fontSize: 16, bgcolor: 'primary.main' }}
                    >
                      {!m.avatar_url && initials}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fullName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.kind === 'primary' ? 'Primary Mentor' : 'Class Mentor'}
                      </Typography>
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Office hours */}
      {classMentors.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <ClassOfficeHoursSection mentors={classMentors} editable={false} />
        </Box>
      )}

      {/* Upcoming */}
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
        Upcoming
      </Typography>
      {upcoming.length === 0 ? (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography color="text.secondary">No upcoming meetings.</Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 3 }}>
          {upcoming.map(m => (
            <MeetingCard
              key={m.meeting_id} m={m}
              onRSVP={handleRSVP}
              actioning={actioning === m.meeting_id}
              canRSVP={viewerIsStudent}
            />
          ))}
        </Box>
      )}

      {/* Past */}
      {past.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
            Past meetings
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {past.slice().reverse().map(m => (
              <MeetingCard key={m.meeting_id} m={m} onRSVP={handleRSVP} actioning={false} canRSVP={viewerIsStudent} />
            ))}
          </Box>
        </>
      )}
    </Box>
  )
}

function MeetingCard({
  m, onRSVP, actioning, canRSVP,
}: {
  m: Meeting; onRSVP: (id: string, attend: boolean) => void; actioning: boolean; canRSVP: boolean
}) {
  const upcoming = new Date(m.scheduled_at).getTime() >= Date.now()
  const isOfficeHour = m.kind === 'office_hours'

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <EventIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fmtDateTime(m.scheduled_at)}
              </Typography>
              <Typography variant="caption" color="text.secondary">· {m.duration_min} min</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              <Chip size="small" label={isOfficeHour ? 'Office Hours' : 'Group'} color={isOfficeHour ? 'secondary' : 'primary'} variant="outlined" />
              <Chip size="small" label={FORMAT_LABEL[m.format]} />
            </Box>
            {m.host_name && (
              <Typography variant="caption" color="text.secondary">Host: {m.host_name}</Typography>
            )}
          </Box>
          <AttendanceTag status={m.my_status} upcoming={upcoming} />
        </Box>

        {(m.format === 'in_person' || m.format === 'simulcast') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Typography variant="caption">{m.location}</Typography>
          </Box>
        )}
        {(m.format === 'online' || m.format === 'simulcast') && (m.zoom_id || m.zoom_passcode || m.zoom_url) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
            <VideocamIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            {/* The join URL was stored and fetched but never rendered, so a
                student saw digits to retype instead of a link to press. */}
            {m.zoom_url ? (
              <Typography variant="caption">
                <MuiLink href={m.zoom_url} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
                  Join on Zoom
                </MuiLink>
                {m.zoom_id && ` · ID ${m.zoom_id}`}
                {m.zoom_passcode && ` · Passcode ${m.zoom_passcode}`}
              </Typography>
            ) : (
              <Typography variant="caption">
                Zoom · ID {m.zoom_id || '—'}{m.zoom_passcode && ` · Passcode ${m.zoom_passcode}`}
              </Typography>
            )}
          </Box>
        )}
        {m.notes && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
            {m.notes}
          </Typography>
        )}

        {m.recording_url && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlayCircleIcon />}
            href={m.recording_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1.5 }}
          >
            Watch recording
          </Button>
        )}

        {/* Office-hour RSVP buttons */}
        {isOfficeHour && upcoming && canRSVP && (
          <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
            {m.my_status === 'planning' ? (
              <Button
                size="small"
                color="warning"
                variant="outlined"
                disabled={actioning}
                onClick={() => onRSVP(m.meeting_id, false)}
              >
                {actioning ? <CircularProgress size={16} /> : 'Withdraw RSVP'}
              </Button>
            ) : (
              <Button
                size="small"
                color="primary"
                variant="contained"
                disabled={actioning}
                onClick={() => onRSVP(m.meeting_id, true)}
              >
                {actioning ? <CircularProgress size={16} /> : 'I plan to attend'}
              </Button>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

function AttendanceTag({ status, upcoming }: { status: string | null; upcoming: boolean }) {
  if (!status) return null
  if (upcoming && status === 'planning') return <Chip size="small" color="info" label="RSVP'd" />
  if (status === 'present') return <Chip size="small" color="success" label="Attended" />
  if (status === 'absent')  return <Chip size="small" color="error"   label="Missed" />
  if (status === 'excused') return <Chip size="small" color="warning" label="Excused" />
  if (status === 'no_show') return <Chip size="small" color="error"   label="No-show" />
  return null
}
